import TEALIUM from "../../enums/tealium-enums";
import { track } from "../../util/tealium";

/**
 * Track annual dashboard page view
 * page_category: "performance_dashboard"
 * page_detail: "<persona>:annual:<py_xx>"
 * page_section: "annual_dashboard"
 */
export const trackAnnualDashboardPageView = (persona: number, selectedPeriod: string) => {
  track(
    TEALIUM.U_TAG_METHOD.VIEW,
    "",
    TEALIUM.SECTION.ANNUAL_DASHBOARD,
    TEALIUM.CATEGORY.PERFORMANCE_DASHBOARD,
    `${persona}:annual:${selectedPeriod}`,
    "",
  );
};

export const trackMonthlyDashboardPageView = (persona: number, selectedPeriod: string) => {
  track(
    TEALIUM.U_TAG_METHOD.VIEW,
    "",
    TEALIUM.SECTION.MONTHLY_DASHBOARD,
    TEALIUM.CATEGORY.PERFORMANCE_DASHBOARD,
    `${persona}:monthly:${selectedPeriod}`,
    "",
  );
};

export const trackPerformanceIncomeToggle = (targetDashboard: string) => {
  const category = targetDashboard === "annual" ? TEALIUM.SECTION.ANNUAL_DASHBOARD : TEALIUM.SECTION.MONTHLY_DASHBOARD;
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.PERFORMANCE_AND_INCOME,
    category,
    "",
    "performance_&_income_toggle",
  );
};

export const trackShowDataToggle = (isOn: boolean) => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.MONTHLY_DASHBOARD,
    `show_data_${isOn ? "on" : "off"}`,
    "",
    "show_data",
  );
};

export const trackMonthlyInfoIcon = (isOpen: boolean) => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.MONTHLY_DASHBOARD,
    `info_modal_${isOpen ? "open" : "close"}`,
    "",
    "info_icon",
  );
};

export const trackCustomisedKpis = (isOpen: boolean) => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.MONTHLY_DASHBOARD,
    `customised_kpis_modal_${isOpen ? "open" : "close"}`,
    "",
    "customised_kpis",
  );
};

export const trackPersonalBonusModal = (isOpen: boolean) => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.MONTHLY_DASHBOARD,
    `personal_modal_${isOpen ? "open" : "close"}`,
    "",
    "personal",
  );
};

export const trackDifferentialBonusModal = (isOpen: boolean) => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.MONTHLY_DASHBOARD,
    `differential_modal_${isOpen ? "open" : "close"}`,
    "",
    "differential",
  );
};

export const trackRubyBonusModal = (isOpen: boolean) => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.MONTHLY_DASHBOARD,
    `ruby_modal_${isOpen ? "open" : "close"}`,
    "",
    "ruby",
  );
};

const camelToSnake = (str: string): string => str.replace(/([A-Z])/g, "_$1").toLowerCase();

const INCENTIVE_TEALIUM_NAMES: Record<string, string> = {
  ppe: "performance_plus_and_elite",
  performancePlus: "performance_plus",
  performanceElite: "performance_plus_and_elite",
  monthlyQ: "Monthly_Q1/QV",
  qGrowthIncentive: "Q_Growth_Incentive",
  monthlyPgg: "personal_group_growth",
  pga: "personal_growth_annual",
  pgg: "personal_group_growth",
  fgi: "frontline-growth",
  ttci: "two_time_cash_incentive",
  ssi: "strong_start_incentive",
  csi: "customer_sales_incentive",
  faa: "faa",
  faaAwardTracking: "faa_award_tracking",
  hatgar: "hat",
  hatLegsTracking: "hat_legs_tracking",
  garTracking: "gar_tracking",
  qcGrowth: "QC_growth",
  visionBonus: "vision_bonus",
  healthWellbeing: "health_and_wellbeing",
  // Annual API labels are navigation keys; keep historical event values stable.
  annualq: "annual_Q_incentive",
  qcg: "QC_growth",
  personalGroupGrowthGCR: "pgg_gcr",
  personalGrowthAnnual: "personal_growth_annual",
  personalGroupGrowth: "personal_group_growth",
  personalGroupTieredIncentive: "pgti",
  twoTimeCashIncentive: "two_time_cash_incentive",
  frontlineGrowth: "frontline-growth",
  annualQ: "annual_Q_incentive",
  frontlineQ: "Frontline_Q",
  diamondBonus: "100_diamond",
  diamondA70: "100_diamond",
  leadershipPlus: "leadership_plus",
  leadershipSeminar: "leadership_seminar",
  pathToA70: "100_diamond",
  a70EliteBonus: "100_diamond",
  "bronzeIncentives.ptb": "path_to_bronze_incentive",
  "bronzeIncentives.bfi": "bronze_foundation_incentive",
  "bronzeIncentives.bbi": "bronze_builder_incentive",
  pathToBronze: "path_to_bronze_incentive",
  bronzeFoundation: "bronze_foundation_incentive",
  bronzeBuilder: "bronze_builder_incentive",
  strongStartIncentive: "strong_start_incentive",
  personalGrowthMonthly: "personal_group_growth",
  foundersSalesIncentive: "founders_sales_incentive",
};

const toTealiumIncentiveName = (contentKey: string): string => {
  return INCENTIVE_TEALIUM_NAMES[contentKey] ?? camelToSnake(contentKey);
};

export const trackMonthlyTabSelection = (contentKey: string) => {
  const incentiveName = toTealiumIncentiveName(contentKey);
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.MONTHLY_DASHBOARD,
    incentiveName,
    "",
    incentiveName,
  );
};

const NAV_ITEM_TEALIUM_NAMES: Record<string, string> = {
  mbi: "my_business_income",
  earningsCalculator: "calculator",
  onlineStatements: "online_statement",
};

const toTealiumNavName = (itemId: string): string => {
  return NAV_ITEM_TEALIUM_NAMES[itemId] ?? camelToSnake(itemId);
};

export const trackMonthlyNavTileClick = (itemId: string) => {
  const name = toTealiumNavName(itemId);
  track(TEALIUM.U_TAG_METHOD.LINK, TEALIUM.EVENT_NAME.CLICK_ACTION, TEALIUM.SECTION.MONTHLY_DASHBOARD, name, "", name);
};

export const trackAnnualInfoIcon = (isOpen: boolean) => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.ANNUAL_DASHBOARD,
    `info_modal_${isOpen ? "open" : "close"}`,
    "",
    "info_icon",
  );
};

export const trackLeadershipTripQualification = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.ANNUAL_DASHBOARD,
    TEALIUM.CATEGORY.LEADERSHIP_SEMINAR,
    "",
    "leadership_trip_qualification",
  );
};

export const trackHatLegsTracking = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.ANNUAL_DASHBOARD,
    TEALIUM.CATEGORY.HAT_LEGS_TRACKING,
    "",
    "hat_legs_tracking",
  );
};

export const trackGarTracking = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.ANNUAL_DASHBOARD,
    TEALIUM.CATEGORY.GAR_TRACKING,
    "",
    "gar_tracking",
  );
};

export const trackFaaAwardTracking = () => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.ANNUAL_DASHBOARD,
    TEALIUM.CATEGORY.FAA,
    "",
    "faa_award_tracking",
  );
};

export const trackFoundersPlatinumStatus = (isOpen: boolean) => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.ANNUAL_DASHBOARD,
    `fpp_modal_${isOpen ? "open" : "close"}`,
    "",
    "founders_platinum_status",
  );
};

export const trackGlobalAwardTrackingLevel = (isOpen: boolean) => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.ANNUAL_DASHBOARD,
    `global_award_tracking_level_${isOpen ? "open" : "close"}`,
    "",
    "global_award_tracking_level",
  );
};

export const trackAnnualIncentiveClick = (incentiveName: string) => {
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.ANNUAL_DASHBOARD,
    incentiveName,
    "",
    incentiveName,
  );
};

export const trackAnnualNavTileClick = (itemId: string) => {
  const name = toTealiumNavName(itemId);
  track(
    TEALIUM.U_TAG_METHOD.LINK,
    TEALIUM.EVENT_NAME.CLICK_ACTION,
    TEALIUM.SECTION.ANNUAL_DASHBOARD,
    name,
    "",
    `tab_selection_${name}`,
  );
};

// ─── Shared Incentive Tile Click Tracking (used by PerformanceTileHeader) ────

/**
 * Tiles whose click_action differs from the default "tab_selection_<name>" pattern.
 * Key = tealiumTag value, value = { action, category }.
 */
const CUSTOM_TILE_ACTIONS: Record<string, { action: string; category: string }> = {
  hatLegsTracking: { action: "hat_legs_tracking", category: "hat_legs_tracking" },
  garTracking: { action: "gar_tracking", category: "gar_tracking" },
  faaAwardTracking: { action: "faa_award_tracking", category: "faa" },
};

/**
 * Track incentive tile click from PerformanceTileHeader.
 * Automatically determines monthly vs annual section from the URL hash.
 * Custom overrides for HAT, GAR, FAA tiles.
 */
export const trackIncentiveTileClick = (contentKey: string) => {
  const isAnnual = window.location.hash === "#annual";
  const section = isAnnual ? TEALIUM.SECTION.ANNUAL_DASHBOARD : TEALIUM.SECTION.MONTHLY_DASHBOARD;

  const custom = CUSTOM_TILE_ACTIONS[contentKey];
  if (custom) {
    track(TEALIUM.U_TAG_METHOD.LINK, TEALIUM.EVENT_NAME.CLICK_ACTION, section, custom.category, "", custom.action);
    return;
  }

  const incentiveName = toTealiumIncentiveName(contentKey);
  track(TEALIUM.U_TAG_METHOD.LINK, TEALIUM.EVENT_NAME.CLICK_ACTION, section, incentiveName, "", incentiveName);
};
